// Export: a Markdown report for people and a JSON report for programs.
import { composition, fieldsOf, kids, timeOf, type Model } from '../model';
import type { Segment } from '../parsers/types';
import { fmtBytes, fmtHz } from '../parsers/bits';
import { KIND_LABEL, KIND_ORDER } from '../content/kinds';
import { fmtTime } from './anatomy';

const MAX_LIST = 12; // children listed per block before summarizing

export function markdownReport(m: Model): string {
  const pf = m.pf, a = pf.audio;
  const L: string[] = [];
  L.push(`# ${m.name}`, '');
  L.push(`Generated by [audiofile.info](https://audiofile.info) on ${new Date().toISOString().slice(0, 10)}.`, '');
  L.push('## Summary', '', '| | |', '|---|---|');
  const row = (k: string, v: string | undefined | null | false) => v && L.push(`| ${k} | ${v.replace(/\|/g, '\\|')} |`);
  row('Container', pf.container);
  row('Codec', `${a.codec}${a.codecLong ? ` (${a.codecLong})` : ''}`);
  row('Sample rate', a.sampleRate ? fmtHz(a.sampleRate) : null);
  row('Channels', a.channels ? `${a.channels}${a.channelMode ? ` (${a.channelMode})` : ''}` : null);
  row('Bit depth', a.bitsPerSample ? `${a.bitsPerSample}-bit` : null);
  row('Duration', m.duration ? `${fmtTime(m.duration, 1)} (${m.duration.toFixed(3)} s)` : null);
  row('Bitrate', a.bitrate ? `${Math.round(a.bitrate / 1000)} kbps${a.bitrateMode ? ` ${a.bitrateMode}` : ''}` : null);
  row('Lossless', a.lossless ? 'yes' : 'no');
  row('Encoder', a.encoder);
  row('Size', `${pf.size.toLocaleString('en-US')} bytes (${fmtBytes(pf.size)})`);
  L.push('');

  const comp = composition(m);
  L.push('## Composition', '', '| Block type | Bytes | Share |', '|---|---:|---:|');
  for (const k of KIND_ORDER) {
    const n = comp.get(k);
    if (n) L.push(`| ${KIND_LABEL[k]} | ${n.toLocaleString('en-US')} | ${((n / pf.size) * 100).toFixed(2)}% |`);
  }
  L.push('');

  if (pf.tags.length) {
    L.push('## Tags', '', '| Tag | Stored as | Value |', '|---|---|---|');
    for (const t of pf.tags) L.push(`| ${t.key} | \`${t.rawKey}\` | ${t.value.replace(/\|/g, '\\|').replace(/\n/g, ' ').slice(0, 300)} |`);
    L.push('');
  }
  if (pf.notes.length) {
    L.push('## Observations', '');
    for (const n of pf.notes) L.push(`- ${n.level === 'warn' ? '⚠️ ' : ''}${n.text}`);
    L.push('');
  }

  L.push('## Structure', '', '```');
  const walk = (list: Segment[], depth: number) => {
    const show = list.length > MAX_LIST + 2 ? list.slice(0, MAX_LIST) : list;
    for (const s of show) {
      const tr = timeOf(m, s);
      L.push(`${'  '.repeat(depth)}${pad(s.start)}  ${s.name}  [${fmtBytes(s.end - s.start)}]${s.summary ? `  ${s.summary}` : ''}${tr && depth === 0 ? `  (${fmtTime(tr[0], 1)}–${fmtTime(tr[1], 1)})` : ''}`);
      if (depth < 4 && Array.isArray(s.children)) walk(s.children, depth + 1);
      else if (depth < 2 && typeof s.children === 'function' && s.kind !== 'audio') walk(kids(s), depth + 1);
    }
    if (show.length < list.length) L.push(`${'  '.repeat(depth)}… and ${(list.length - show.length).toLocaleString('en-US')} more`);
  };
  walk(m.top, 0);
  L.push('```', '');

  // Fields of the non-audio top-level blocks (headers, tags, indexes).
  L.push('## Header fields', '');
  for (const s of m.top.slice(0, 40)) {
    const fs = fieldsOf(s);
    if (!fs.length || s.kind === 'audio') continue;
    L.push(`### ${s.name} @ ${s.start}`, '', '| Field | Value |', '|---|---|');
    for (const f of fs.slice(0, 40)) L.push(`| ${f.name} | ${f.value.replace(/\|/g, '\\|').replace(/\n/g, ' ').slice(0, 200)} |`);
    L.push('');
  }
  return L.join('\n');
}

const pad = (n: number) => n.toString(16).toUpperCase().padStart(8, '0');

export function jsonReport(m: Model): string {
  const pf = m.pf;
  const seg = (s: Segment, depth: number): unknown => {
    const o: Record<string, unknown> = { name: s.name, type: s.type, kind: s.kind, start: s.start, end: s.end };
    if (s.summary) o.summary = s.summary;
    if (s.time) o.time = s.time;
    if (s.warning) o.warning = s.warning;
    if (s.kind !== 'audio' || depth === 0) {
      const f = fieldsOf(s);
      if (f.length) o.fields = f.map((x) => ({ name: x.name, value: x.value, bit: x.bit, bits: x.bits }));
    }
    // Frames are listed compactly below; do not repeat them here.
    const ch = Array.isArray(s.children) ? s.children : s.kind !== 'audio' && depth < 3 && s.children ? kids(s) : [];
    const framesInside = ch.length > 50 && ch.every((c) => c.time);
    if (ch.length && !framesInside && depth < 6) o.children = ch.map((c) => seg(c, depth + 1));
    else if (framesInside) o.childCount = ch.length;
    return o;
  };
  return JSON.stringify(
    {
      generator: 'audiofile.info',
      file: { name: m.name, size: pf.size },
      format: pf.format, container: pf.container, audio: pf.audio, tags: pf.tags, notes: pf.notes,
      pictures: pf.pictures,
      segments: m.top.map((s) => seg(s, 0)),
      frames: { columns: ['start', 'end', 't0', 't1'], rows: m.frames.map((f) => [f.start, f.end, +f.time![0].toFixed(6), +f.time![1].toFixed(6)]) },
    },
    null,
    1,
  );
}
